import { useEffect } from "react";

import { connectSocket, disconnectSocket } from "@/lib/socket";
import { getAccessToken } from "@/lib/token-storage";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSocketStatus } from "@/store/socket/socketSlice";

/**
 * The socket connection's only lifecycle owner — connects once
 * authenticated, disconnects on logout. Mounted once at the app root
 * (`SocketConnectionManager`, rendered inside `App.tsx`), not per-page: a
 * chat page unmounting (navigating away) must not drop the connection, or
 * a `message_received` for a *different* conversation would be missed
 * while browsing the rest of the site.
 */
export function useSocketConnection(): void {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.status === "authenticated");

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      dispatch(setSocketStatus("disconnected"));
      return;
    }

    const token = getAccessToken();
    if (!token) {
      return;
    }

    dispatch(setSocketStatus("connecting"));
    const socket = connectSocket(token);

    function handleConnect() {
      dispatch(setSocketStatus("connected"));
    }
    function handleDisconnect() {
      dispatch(setSocketStatus("disconnected"));
    }
    function handleConnectError() {
      dispatch(setSocketStatus("error"));
    }

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
    };
  }, [isAuthenticated, dispatch]);
}
